import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

interface HorizontalScrollRegionProps {
  children: ReactNode
  className?: string
  label: string
  activeSelector?: string
}

export function HorizontalScrollRegion({
  children,
  className = '',
  label,
  activeSelector,
}: HorizontalScrollRegionProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ canScroll: false, atStart: true, atEnd: true })

  const measure = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const canScroll = viewport.scrollWidth - viewport.clientWidth > 1
    const atStart = !canScroll || viewport.scrollLeft <= 1
    const atEnd = !canScroll || viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 1
    setEdges((current) =>
      current.canScroll === canScroll && current.atStart === atStart && current.atEnd === atEnd
        ? current
        : { canScroll, atStart, atEnd },
    )
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const frame = requestAnimationFrame(measure)
    const observer = new ResizeObserver(measure)
    observer.observe(viewport)
    if (viewport.firstElementChild) observer.observe(viewport.firstElementChild)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [children, measure])

  useEffect(() => {
    if (!activeSelector) return
    const viewport = viewportRef.current
    const active = viewport?.querySelector<HTMLElement>(activeSelector)
    if (!viewport || !active) return
    if (viewport.scrollWidth - viewport.clientWidth <= 1) return
    active.scrollIntoView({ block: 'nearest', inline: 'center' })
    const frame = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(frame)
  }, [activeSelector, children, measure])

  return (
    <div
      className={`horizontal-scroll-region${edges.canScroll ? ' can-scroll' : ''}${edges.atStart ? ' at-start' : ''}${edges.atEnd ? ' at-end' : ''}`}
    >
      <div
        ref={viewportRef}
        className={`horizontal-scroll-viewport ${className}`.trim()}
        role="region"
        aria-label={label}
        tabIndex={edges.canScroll ? 0 : undefined}
        onScroll={measure}
      >
        {children}
      </div>
      {edges.canScroll ? <span className="sr-only">Scroll horizontally to see more.</span> : null}
    </div>
  )
}
